import { CREDIT_MEMO_KINDS } from '../types';
import type { Bill, Invoice } from '../types';

// ERP [37]: 'sent' is the open AR lifecycle state; the amount_due > 0 filter
// excludes fully-settled invoices (payment is a separate axis now).
const OPEN_STATUSES = new Set(['sent']);

export interface AllocatableInvoices {
  /** Open, same-currency invoices the receipt can be applied to. */
  openInvoices: Invoice[];
  /** The currency all shown invoices share (a payment settles one currency). */
  allocationCurrency: string | undefined;
}

/**
 * Given a customer's invoices, pick the ones a receipt can be allocated to:
 * open (`sent`, positive balance, non-credit-memo), narrowed to a single
 * currency. A payment settles one currency, so we anchor to the source invoice's
 * currency (invoice-driven flow) or the first open invoice, and drop the rest —
 * the user records a separate receipt for a different-currency invoice.
 */
export function selectAllocatableInvoices(
  invoices: Invoice[],
  defaultInvoiceId?: string,
): AllocatableInvoices {
  const open = invoices.filter(
    (inv) =>
      !CREDIT_MEMO_KINDS.includes(inv.kind) &&
      OPEN_STATUSES.has(inv.status) &&
      Number(inv.amount_due) > 0,
  );
  if (open.length === 0) return { openInvoices: [], allocationCurrency: undefined };
  const anchor = open.find((inv) => inv.id === defaultInvoiceId) ?? open[0];
  const allocationCurrency = anchor.currency_code;
  return {
    openInvoices: open.filter((inv) => inv.currency_code === allocationCurrency),
    allocationCurrency,
  };
}

/**
 * Greedy allocation across open invoices. With a positive `budget`, fill each
 * invoice's balance in order up to the budget; with no budget (0 / NaN), fill
 * every balance in full. Amounts are rounded to cents.
 */
export function autoAllocateAmounts(
  openInvoices: Invoice[],
  budget: number,
): Record<string, number> {
  const unbounded = !budget || budget <= 0;
  let left = budget;
  const next: Record<string, number> = {};
  for (const inv of openInvoices) {
    if (!unbounded && left <= 0) break;
    const take = unbounded ? Number(inv.amount_due) : Math.min(Number(inv.amount_due), left);
    next[inv.id] = Math.round(take * 100) / 100;
    if (!unbounded) left -= take;
  }
  return next;
}

/** Sum the per-invoice allocation amounts (ignoring null/blank entries). */
export function sumAllocations(amounts: Record<string, number | null>): number {
  return Object.values(amounts).reduce((sum: number, v) => sum + (Number(v) || 0), 0);
}

// ---------------------------------------------------------------------------
// AP mirror — open vendor bills a made payment can be applied to. Bills use a
// different open lifecycle (`due`) than invoices (`sent`), and vendor credits
// (negative-total credit notes) are excluded the same way credit memos are on
// the AR side.
// ---------------------------------------------------------------------------

export interface AllocatableBills {
  /** Open, same-currency bills the vendor payment can be applied to. */
  openBills: Bill[];
  /** The currency all shown bills share (a payment settles one currency). */
  allocationCurrency: string | undefined;
}

/**
 * Given a vendor's bills, pick the ones a made payment can be allocated to:
 * open (`due`, positive balance, kind `bill` — not a vendor credit), narrowed to
 * a single currency. Anchors to the source bill's currency (bill-driven flow) or
 * the first open bill and drops the rest — the user records a separate payment
 * for a different-currency bill. Mirrors {@link selectAllocatableInvoices}.
 */
export function selectAllocatableBills(
  bills: Bill[],
  defaultBillId?: string,
): AllocatableBills {
  const open = bills.filter(
    (bill) =>
      bill.kind === 'bill' &&
      bill.status === 'due' &&
      Number(bill.amount_due) > 0,
  );
  if (open.length === 0) return { openBills: [], allocationCurrency: undefined };
  const anchor = open.find((bill) => bill.id === defaultBillId) ?? open[0];
  const allocationCurrency = anchor.currency_code;
  return {
    openBills: open.filter((bill) => bill.currency_code === allocationCurrency),
    allocationCurrency,
  };
}

/**
 * Greedy allocation across open bills — the {@link autoAllocateAmounts} mirror.
 * With a positive `budget`, fill each bill's balance in order up to the budget;
 * with no budget (0 / NaN), fill every balance in full. Rounded to cents.
 */
export function autoAllocateBillAmounts(
  openBills: Bill[],
  budget: number,
): Record<string, number> {
  const unbounded = !budget || budget <= 0;
  let left = budget;
  const next: Record<string, number> = {};
  for (const bill of openBills) {
    if (!unbounded && left <= 0) break;
    const take = unbounded ? Number(bill.amount_due) : Math.min(Number(bill.amount_due), left);
    next[bill.id] = Math.round(take * 100) / 100;
    if (!unbounded) left -= take;
  }
  return next;
}
